import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { TextField, Button, MenuItem, Box, Typography, Stack, Alert, CircularProgress, Dialog, DialogTitle, DialogActions, Collapse, Drawer, List, ListItem, IconButton, Card, CardContent, CardActions } from '@mui/material'
import { Search as SearchIcon, Add as AddIcon, ArrowBack as ArrowBackIcon, Delete as DeleteIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import { AuthContext } from '../../context/AuthContext';
import api from '../../services/api';

const EditRecipe = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { user } = useContext(AuthContext);

  // Determine basePath based on current route (admin or coach)
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isCoach = user?.role === 'coach';
  const basePath = isAdminRoute ? '/admin' : '/coach';

  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [ingredientList, setIngredientList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIngredient, setSelectedIngredient] = useState(null);
  const [quantity, setQuantity] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [successModalOpen, setSuccessModalOpen] = useState(false);

  const INTOLERANCE_OPTIONS = [
    { value: 'gluten', label: 'Gluten' },
    { value: 'lactose', label: 'Lactose' },
    { value: 'nuts', label: 'Noix' },
    { value: 'shellfish', label: 'Fruits de mer' },
    { value: 'soy', label: 'Soja' },
    { value: 'eggs', label: 'Œufs' },
    { value: 'fish', label: 'Poisson' },
    { value: 'pork', label: 'Porc' },
    { value: 'beef', label: 'Bœuf' },
    { value: 'chicken', label: 'Poulet' },
    { value: 'turkey', label: 'Dinde' },
    { value: 'corn', label: 'Maïs' },
    { value: 'sesame', label: 'Sésame' },
    { value: 'mustard', label: 'Moutarde' },
    { value: 'peanuts', label: 'Arachides' },
    { value: 'citrus', label: 'Agrumes' },
    { value: 'mushrooms', label: 'Champignons' },
    { value: 'legumes', label: 'Légumineuses' },
    { value: 'other', label: 'Autre' },
  ];  

  useEffect(() => {
    // Fetch recipe details
    const fetchRecipe = async () => {
      try {
        setLoading(true);

        if (isAdminRoute) {
          // Admin: fetch from admin global recipes API
          const data = await api.get(`/admin/recipes/${id}`);
          setRecipe(data);
        } else {
          // Coach: fetch from multi-tenant API
          try {
            const data = await api.get(`/coach/recipes/${id}`);
            // Check if coach owns this recipe (source: 'coach')
            if (data.source === 'global') {
              setAccessDenied(true);
              return;
            }
            setRecipe(data);
          } catch (err) {
            if (err.message?.includes('403') || err.status === 403) {
              setAccessDenied(true);
              return;
            }
            throw err;
          }
        }
      } catch (error) {
        console.error('Erreur lors de la recuperation de la recette:', error);
        setErrorMessage('Impossible de charger la recette.');
        setShowAlert(true);
      } finally {
        setLoading(false);
      }
    };

    fetchRecipe();

    // Fetch ingredient list from ciqual.json
    fetch('/ciqual.json')
      .then(response => response.json())
      .then(data => {
        setIngredientList(data);
      });
  }, [id, isAdminRoute]);

  const parseNutrientValue = (value) => {
    if (typeof value === 'string') {
      value = value.replace(',', '.');
      if (value.includes('<') || value === 'traces' || value === '-' || value === '') {
        return 0;
      }
    }
    return parseFloat(value) || 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setRecipe({ ...recipe, [name]: value });
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const toggleDrawer = (open) => () => {
    setDrawerOpen(open);
    setSearchTerm('');
  };

  const handleIngredientSelect = (ingredient) => {
    setSelectedIngredient(ingredient);
    setSearchTerm(ingredient.alim_nom_fr);
    setQuantity('');
  };

  const filteredIngredients = ingredientList.filter(ingredient =>
    ingredient.alim_nom_fr.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calcule les macros pour une liste d'ingrédients donnée
  const calculateMacrosForIngredients = (ingredients) => {
    let totalCarbs = 0;
    let totalProteins = 0;
    let totalFats = 0;

    ingredients.forEach(ingredient => {
      const ciqualData = ingredientList.find(item => parseInt(item.alim_code) === parseInt(ingredient.ciqual_code));
      if (ciqualData) {
        const quantityInGrams = ingredient.quantity;
        totalCarbs += (parseNutrientValue(ciqualData["Glucides (g/100 g)"]) * quantityInGrams) / 100;
        totalProteins += (parseNutrientValue(ciqualData["Protéines, N x facteur de Jones (g/100 g)"]) * quantityInGrams) / 100;
        totalFats += (parseNutrientValue(ciqualData["Lipides (g/100 g)"]) * quantityInGrams) / 100;
      }
    });

    const recalculatedCalories = (totalCarbs * 4) + (totalProteins * 4) + (totalFats * 9);

    return {
      calories: recalculatedCalories,
      carbohydrates: totalCarbs,
      proteins: totalProteins,
      fats: totalFats
    };
  };

  const addIngredient = () => {
    if (selectedIngredient && quantity) {
      setRecipe(prevRecipe => {
        const newIngredients = [
          ...prevRecipe.ingredients,
          { name: selectedIngredient.alim_nom_fr, ciqual_code: selectedIngredient.alim_code, quantity: parseFloat(quantity) }
        ];
        const newMacros = calculateMacrosForIngredients(newIngredients);
        return {
          ...prevRecipe,
          ingredients: newIngredients,
          macros: newMacros
        };
      });
      setSelectedIngredient(null);
      setQuantity('');
      setDrawerOpen(false);
    }
  };

  const deleteIngredient = (indexToDelete) => {
    setRecipe(prevRecipe => {
      const newIngredients = prevRecipe.ingredients.filter((_, index) => index !== indexToDelete);
      const newMacros = calculateMacrosForIngredients(newIngredients);
      return {
        ...prevRecipe,
        ingredients: newIngredients,
        macros: newMacros
      };
    });
  };

  const handleSubmit = async () => {
    // Frontend validation
    const missingFields = [];
    if (!recipe.name?.trim()) missingFields.push('Nom de la recette');
    if (!recipe.mealType) missingFields.push('Type de repas');
    if (!recipe.ingredients || recipe.ingredients.length === 0) missingFields.push('Au moins un ingredient');
    if (!recipe.instructions?.trim()) missingFields.push('Instructions');

    if (missingFields.length > 0) {
      setErrorMessage(`Champs requis manquants : ${missingFields.join(', ')}`);
      setShowAlert(true);
      return;
    }

    try {
      if (isAdminRoute) {
        // Admin: update via admin global recipes API
        // Only send allowed fields to avoid validation errors
        const recipeData = {
          name: recipe.name.trim(),
          description: recipe.description || '',
          mealType: recipe.mealType,
          category: Array.isArray(recipe.category) ? recipe.category : (recipe.category ? [recipe.category] : []),
          intolerances: recipe.intolerances || [],
          preparationTime: Number(recipe.preparationTime) || 0,
          cookingTime: Number(recipe.cookingTime) || 0,
          servings: recipe.servings,
          difficulty: recipe.difficulty,
          ingredients: recipe.ingredients.map(ing => ({
            name: ing.name,
            ciqual_code: String(ing.ciqual_code),
            quantity: ing.quantity,
            unit: ing.unit || 'g'
          })),
          instructions: recipe.instructions.trim(),
          macros: recipe.macros,
        };
        await api.put(`/admin/recipes/${id}`, recipeData);
      } else {
        // Coach: update via multi-tenant API (only own recipes)
        const recipeData = {
          name: recipe.name.trim(),
          description: recipe.description || '',
          mealType: recipe.mealType,
          category: Array.isArray(recipe.category) ? recipe.category : (recipe.category ? [recipe.category] : []),
          intolerances: recipe.intolerances || [],
          preparationTime: Number(recipe.preparationTime) || 0,
          cookingTime: Number(recipe.cookingTime) || 0,
          servings: recipe.servings,
          difficulty: recipe.difficulty,
          ingredients: recipe.ingredients.map(ing => ({
            name: ing.name,
            ciqual_code: String(ing.ciqual_code),
            quantity: ing.quantity,
            unit: ing.unit || 'g'
          })),
          instructions: recipe.instructions.trim(),
          macros: recipe.macros,
        };
        await api.put(`/coach/recipes/${id}`, recipeData);
      }

      setSuccessModalOpen(true);
    } catch (error) {
      console.error('Erreur lors de la mise a jour de la recette:', error);
      setErrorMessage(`Erreur lors de la mise a jour de la recette : ${error.message}`);
      setShowAlert(true);
    }
  };
  

  // Access denied - coach trying to edit library recipe
  if (accessDenied) {
    return (
      <Box sx={{ maxWidth: 480, mx: 'auto', mt: 4 }}>
        <Helmet>
          <title>Acces refuse - {isAdminRoute ? 'Admin' : 'Coach'} Yoco</title>
        </Helmet>
        <Alert severity="error" sx={{ mb: 3 }}>
          Vous ne pouvez pas modifier les recettes de la bibliotheque. Seules vos propres recettes peuvent etre editees.
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`${basePath}/recipes`)}
        >
          Retour aux recettes
        </Button>
      </Box>
    );
  }

  if (loading || !recipe) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }


  return (
    <>
      <Helmet>
        <title>Editer recette - {isAdminRoute ? 'Admin' : 'Coach'} Yoco</title>
      </Helmet>

      <Box sx={{ maxWidth: '880px' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate(`${basePath}/recipes`)}>
            Retour
          </Button>
        </Stack>

        <Card>
          <CardContent>
            <Typography sx={{ mb: 3 }} variant="h1">Editer la recette</Typography>

      <TextField
        label="Nom de la recette"
        name="name"
        value={recipe.name}
        onChange={handleInputChange}
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label="Type de repas"
        name="mealType"
        value={recipe.mealType}
        onChange={handleInputChange}
        fullWidth
        sx={{ mb: 2 }}
        select
      >
        <MenuItem value="breakfast">Petit-déjeuner</MenuItem>
        <MenuItem value="lunch">Déjeuner</MenuItem>
        <MenuItem value="dinner">Dîner</MenuItem>
        <MenuItem value="snack">Encas</MenuItem>
      </TextField>
      <TextField
        label="Catégorie"
        name="category"
        value={recipe.category}
        onChange={handleInputChange}
        fullWidth
        sx={{ mb: 2 }}
        select
      >
        <MenuItem value="vegetarian">Vegetarien</MenuItem>
        <MenuItem value="vegan">Vegan</MenuItem>
        <MenuItem value="gluten-free">Sans gluten</MenuItem>
        <MenuItem value="dairy-free">Sans produits laitiers</MenuItem>
        <MenuItem value="paleo">Paléo</MenuItem>
        <MenuItem value="keto">Kéto</MenuItem>
        <MenuItem value="other">Autre</MenuItem>
      </TextField>
      <TextField
        label="Temps de préparation (min)"
        name="preparationTime"
        value={recipe.preparationTime}
        onChange={handleInputChange}
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label="Temps de cuisson (min)"
        name="cookingTime"
        value={recipe.cookingTime}
        onChange={handleInputChange}
        fullWidth
        sx={{ mb: 2 }}
      />

      {/* Bouton pour ouvrir le drawer de sélection des ingrédients */}
      <Button variant="contained" color="secondary"  sx={{ width: '100%' }} onClick={toggleDrawer(true)} startIcon={<AddIcon />}>
        Ajouter un ingrédient
      </Button>

      <Drawer anchor="right" open={drawerOpen} onClose={toggleDrawer(false)}>
        <Box sx={{ width: 300, p: 2 }}>
        <Typography variant="h5">Ingrédients</Typography>
        <TextField
            label="Rechercher"
            value={searchTerm}
            onChange={handleSearchChange}
            fullWidth
            sx={{ mb: 2, mt:2 }}
            InputProps={{
              endAdornment: <SearchIcon />
            }}
          />
          <List>
            {filteredIngredients.map(ingredient => (
              <ListItem
                button
                key={ingredient.alim_code}
                onClick={() => handleIngredientSelect(ingredient)}
                selected={selectedIngredient?.alim_code === ingredient.alim_code}
              >
                {ingredient.alim_nom_fr}
                {selectedIngredient?.alim_code === ingredient.alim_code && (
                  <CheckCircleIcon sx={{ ml: 2, color: 'green' }} />
                )}
              </ListItem>
            ))}
          </List>
          <TextField
            label="Quantité (g)"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            fullWidth
            sx={{ mt: 2 }}
            disabled={!selectedIngredient} // Désactiver tant qu'aucun ingrédient n'est sélectionné
          />
          <Button
            variant="contained"
            onClick={addIngredient}
            sx={{ mt: 2, width:'100%', boxShadow:'none' }}
            disabled={!selectedIngredient || !quantity} // Désactiver tant qu'aucun ingrédient ou quantité n'est fourni
          >
            Ajouter
          </Button>
        </Box>
      </Drawer>

      {/* Liste des ingredients ajoutes */}
      <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {recipe.ingredients.map((ingredient, index) => (
          <Card key={index} sx={{ width: 250 }}>
            <CardContent sx={{ pb: 1 }}>
              <Typography sx={{ mb: 1, lineHeight: 1.3 }} variant="h3">{ingredient.name}</Typography>
              <Typography variant="body2">Quantite : {ingredient.quantity}g</Typography>
              <Typography variant="body2" color="text.secondary">CIQUAL : {ingredient.ciqual_code}</Typography>
            </CardContent>
            <CardActions sx={{ pt: 0 }}>
              <IconButton size="small" aria-label="delete" onClick={() => deleteIngredient(index)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </CardActions>
          </Card>
        ))}
      </Box>

      {/* Affichage des macros calculés */}
      {recipe.macros?.calories >= 0 && (
        <Box sx={{ mt: 2 }}>
            <Typography variant="h6">Calories : {recipe.macros.calories.toFixed(2)} kcal</Typography>
            <Typography variant="body1">Glucides : {recipe.macros.carbohydrates.toFixed(2)} g</Typography>
            <Typography variant="body1">Protéines : {recipe.macros.proteins.toFixed(2)} g</Typography>
            <Typography variant="body1">Lipides : {recipe.macros.fats.toFixed(2)} g</Typography>
        </Box>
      )}


      <TextField
        label="Instructions"
        name="instructions"
        value={recipe.instructions}
        onChange={handleInputChange}
        fullWidth
        multiline
        rows={4}
        sx={{ mt: 4, mb:2 }}
      />

      <TextField
        select
        label="Intolérances"
        name="intolerances"
        value={recipe?.intolerances || []} // Défaut à un tableau vide si les intolérances ne sont pas encore chargées
        onChange={(e) =>
          setRecipe((prevRecipe) => ({
            ...prevRecipe,
            intolerances: e.target.value,
          }))
        }
        fullWidth
        sx={{ mb: 2 }}
        SelectProps={{
          multiple: true, // Permet la sélection multiple
          renderValue: (selected) =>
            selected
              .map(
                (intol) =>
                  INTOLERANCE_OPTIONS.find((opt) => opt.value === intol)?.label || intol
              )
              .join(', '),
        }}
      >
        {INTOLERANCE_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>


      {/* Bouton pour sauvegarder la recette */}
      <Button onClick={handleSubmit} variant="contained" color="primary" sx={{ mt: 2, width: '100%', boxShadow: 'none' }}>
        Sauvegarder
      </Button>

      <Collapse in={showAlert}>
        <Alert
          severity="error"
          onClose={() => setShowAlert(false)}
          sx={{ mb: 2, mt:2 }}
        >
          {errorMessage}
        </Alert>
      </Collapse>

    <Collapse in={!!successMessage}>
      <Alert
        severity="success"
        onClose={() => setSuccessMessage('')}
        sx={{ mb: 2, mt: 2 }}
      >
        {successMessage}
      </Alert>
    </Collapse>

      </CardContent>
      </Card>

        <Dialog open={successModalOpen} onClose={() => setSuccessModalOpen(false)}>
          <DialogTitle>Recette mise a jour avec succes</DialogTitle>
          <DialogActions>
            <Button onClick={() => navigate(`${basePath}/recipes`)} color="primary">
              OK
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default EditRecipe;
